import { z } from "zod"

import { paginatedSchema } from "@/lib/api/pagination"

/**
 * CNI SSH Tunnel Devices (ADR #023).
 *
 * Fiziksel RTU kutuları (HMI Kunos / EC100 / RevPi) ve Hetzner'a kurdukları
 * reverse-SSH tunnel'lar. EMS cihazlarından (cni-device-registry: solar/bess/pcs)
 * TAMAMEN AYRI bir kavram.
 *
 * Backend: cni-ssh-api (Python FastAPI). Response camelCase, pagination APM
 * paginatedSchema zarfıyla aynı. Lookup key = serialNo.
 */

// profileType, backend'in (cni-ssh-api) belirlediği açık uçlu bir cihaz kodudur:
// <üretici>-<model> şeması (örn. "kunos-hmi", "iotrouter-ec100", "kunbus-revpi",
// "moxa-uc"). Yeni cihaz tipleri eklendiğinde APM'in KIRILMAMASI için sabit enum
// yerine serbest string; görünen etiket + rozet rengi CniSshTypeBadge'de
// fallback'li eşlenir (bilinmeyen kod → nötr rozet + ham kod).
export const cniSshProfileSchema = z.string()
export type CniSshProfile = z.infer<typeof cniSshProfileSchema>

export const cniSshStatusSchema = z.enum([
  "pending",
  "provisioning",
  "active",
  "offline",
  "failed",
  "disabled",
])
export type CniSshStatus = z.infer<typeof cniSshStatusSchema>

export const cniSshLogStatusSchema = z.enum(["success", "failed", "in_progress", "skipped"])
export type CniSshLogStatus = z.infer<typeof cniSshLogStatusSchema>

// ── Liste DTO (summary) ──────────────────────────────────────────────────────
export const cniSshDeviceSummaryDtoSchema = z.object({
  id: z.number(),
  serialNo: z.string(),
  hostname: z.string().nullable(),
  profileType: cniSshProfileSchema,
  localIp: z.string().nullable(),
  macAddress: z.string().nullable(),
  tunnelPort: z.number().nullable(),
  status: cniSshStatusSchema,
  location: z.string().nullable(),
  customer: z.string().nullable(),
  provisionedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type CniSshDeviceSummaryDto = z.infer<typeof cniSshDeviceSummaryDtoSchema>

// ── Detay DTO (summary + public_key + notes) ─────────────────────────────────
export const cniSshDeviceDetailDtoSchema = cniSshDeviceSummaryDtoSchema.extend({
  publicKey: z.string().nullable(),
  notes: z.string().nullable(),
})
export type CniSshDeviceDetailDto = z.infer<typeof cniSshDeviceDetailDtoSchema>

// ── Provisioning log ─────────────────────────────────────────────────────────
export const cniSshLogEntrySchema = z.object({
  id: z.number(),
  deviceId: z.number().nullable(),
  step: z.string(),
  status: cniSshLogStatusSchema,
  message: z.string().nullable(),
  errorDetail: z.string().nullable(),
  durationMs: z.number().nullable(),
  createdAt: z.string(),
})
export type CniSshLogEntry = z.infer<typeof cniSshLogEntrySchema>

// ── Agent (.exe) sürüm + indirme ─────────────────────────────────────────────
// Backend /agent/version döndürür. Tüm cihaz aksiyonları (kayıt/SSH/silme) bu
// .exe'de yapılır; APM sadece durumları gösterir + güncel .exe'yi indirtir.
export const cniSshAgentVersionSchema = z.object({
  minVersion: z.string(),
  latestVersion: z.string(),
  downloadUrl: z.string(),
})
export type CniSshAgentVersion = z.infer<typeof cniSshAgentVersionSchema>

// ── Pagination ───────────────────────────────────────────────────────────────
export const paginatedCniSshDevicesSchema = paginatedSchema(cniSshDeviceSummaryDtoSchema)

// ── Domain tipleri (DTO = domain; ekstra dönüşüm gerekmiyor, alanlar zaten temiz) ──
export type CniSshDeviceSummary = CniSshDeviceSummaryDto
export type CniSshDeviceDetail = CniSshDeviceDetailDto

export interface CniSshDeviceListParams {
  page?: number
  pageSize?: number
  search?: string
  type?: CniSshProfile
  status?: CniSshStatus
  customer?: string
}
